import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Clear from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from '../Shared/Icons';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  setAssetTableSearchFilter,
  setAssetTableRefetch,
} from '@/features/filters/filtersSlice';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Theme, useTheme } from '@mui/material';

export type SearchBarProps = {
  search: string;
  variant?: 'filled' | 'outlined' | 'standard';
  borderWidth?: string;
};

const SearchBar = ({
  search,
  variant = 'outlined',
  borderWidth = '1px',
}: SearchBarProps) => {
  const { t } = useTranslation();

  const theme = useTheme<Theme>();

  const assetFiltered = useAppSelector(
    (state) => state.filters.assetTable.assetFiltered,
  );

  const dispatch = useAppDispatch();
  const [enteredText, setEnteredText] = useState<string>('');
  const filterChoose = setAssetTableSearchFilter;
  const location = useLocation();
  const isAssetsPage = location.pathname.indexOf('assets') > -1;
  const handleClear = () => {
    if (filterChoose) {
      setEnteredText('');
      dispatch(filterChoose(''));
    }
  };

  const onClickSearchButton = () => {
    if (enteredText?.length > 0) {
      if (filterChoose) {
        dispatch(filterChoose(enteredText));
        if (assetFiltered.length > 0) {
          dispatch(setAssetTableRefetch(true));
        }
      }
    }
  };
  const onKeyDownHandle = (event: any) => {
    if (event.code === 'Enter') {
      setEnteredText(event.target.value);
      if (filterChoose) {
        dispatch(filterChoose(event.target.value));
      }
    }
  };
  const onChange = (event: any) => {
    if (event.target.value.length === 0) {
      setEnteredText('');
      dispatch(filterChoose(''));
    }
    setEnteredText(event.target.value);
  };

  useEffect(() => {
    if (search && filterChoose) {
      setEnteredText(search);
      dispatch(filterChoose(search));
    }
  }, [search, filterChoose]);

  return (
    <Box
      sx={{
        padding: isAssetsPage ? '0px' : '30px',
        alignItems: 'center',
        position: 'relative',
        maxHeight: '100%',
        width: isAssetsPage ? '100%' : 'inherit',
        display: 'flex',
      }}
    >
      <TextField
        sx={(theme) => ({
          flex: 1,
          verticalAlign: 'top',
          width: '60%',
          '& .MuiOutlinedInput-notchedOutline': {
            border: `${borderWidth} solid!important`,
            borderRadius: '8px!important',
            borderColor: `${theme.palette.text.disabled}!important`,
            legend: { marginLeft: '40px' },
          },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.primary.main}!important`,
          },
          '& .Mui-focused': {
            backgroundColor: `${theme.palette.background.default}!important`,
          },
          color: theme.palette.text.primary,
          position: 'relative',
        })}
        placeholder={t('searchBar.placeHolder')}
        id="filled-hidden-label-small"
        variant={variant}
        size="small"
        value={enteredText}
        inputProps={{
          'data-testid': 'search-bar-input',
          style: { paddingLeft: '20px', width: '100%' },
        }}
        InputProps={{
          endAdornment: enteredText !== '' && (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                onClick={() => handleClear()}
                data-testid="search-bar-clear-button"
              >
                <Clear />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onKeyDown={onKeyDownHandle}
        onChange={onChange}
      />
      <Box
        sx={(theme) => ({
          background: theme.palette.primary.main,
          display: 'flex',
          border: '1px solid #ccc!important',
          height: '40px',
          width: '44px',
          borderRadius: '12px',
          marginLeft: '5px',
          cursor: 'pointer',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        })}
        onClick={onClickSearchButton}
        data-testid="search-button"
      >
        <SearchIcon
          style={{
            color: theme.palette.primary.light,
            paddingLeft: '2px',
            paddingTop: '2px',
            width: '26px',
            height: '26px',
          }}
        />
      </Box>
    </Box>
  );
};

export default SearchBar;
